import { useSuspenseQuery } from '@apollo/client/react';
import { startTransition, useEffect } from 'react';
import { useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';

import { AssetTypeEnum } from 'src/graphql/schema';
import { AssetsDepositSourceDocument } from 'src/graphql/actions/asset/assetsDepositSource.query';
import { GetAssetsCountDocument } from 'src/graphql/actions/asset/getAssetsCount.query';
import { getReportsVersions } from 'src/utils/getReportsVersions';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';

import type { IAssetReportGroupsQueryVariables } from 'src/graphql/actions/reports/assetReportGroups.query';

type Props = {
  setVariables: (variables: Partial<IAssetReportGroupsQueryVariables>) => void;
  setUnsupportedAsset: (isUnsupportedAsset: boolean) => void;
};

const searchSchema = z.object({
  asset: z.string().optional(),
  period: z.string().optional(),
});

type ISearchForm = z.infer<typeof searchSchema>;

const ReportSearch = ({ setVariables, setUnsupportedAsset }: Props) => {
  const { id: softwareEscrowId = '' } = useParams<{
    id: string;
  }>();
  const { data: assetsCount } = useSuspenseQuery(GetAssetsCountDocument, {
    variables: {
      softwareEscrowId: softwareEscrowId,
    },
  });
  const { data: dataAssets } = useSuspenseQuery(AssetsDepositSourceDocument, {
    variables: {
      softwareEscrowId: softwareEscrowId,
      perPage: assetsCount.assets.nodesCount,
    },
  });

  const assetsOptions =
    dataAssets?.assetsSource.nodes.map((asset) => ({
      value: asset.id ?? '',
      label: asset.depositSource ?? '',
    })) || [];

  const onFilterChange = (data: ISearchForm) => {
    const formData: Partial<IAssetReportGroupsQueryVariables> = {};

    setUnsupportedAsset(false);

    if (data?.asset) {
      formData.assetId = data.asset;

      const asset = dataAssets.assetsSource.nodes?.find((item) => item.id === data.asset);

      if (asset?.type !== AssetTypeEnum.OauthRepo) return setUnsupportedAsset(true);
    }

    if (data?.period) formData.period = data.period;

    startTransition(() => setVariables({ ...formData, page: 1 }));
  };

  const form = useForm<ISearchForm>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      asset: '',
      period: '',
    },
    mode: 'onChange',
  });

  const watchedValues = form.watch();

  useEffect(() => onFilterChange(watchedValues), [watchedValues.asset, watchedValues.period]);

  return (
    <Form {...form}>
      <form className="flex items-end justify-between gap-2 md:gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-[776px] max-w-full">
          <FormField
            control={form.control}
            name="asset"
            render={({ field }) => (
              <FormItem>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full mb-0">
                      <SelectValue placeholder="Select asset" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Select asset</SelectLabel>
                    </SelectGroup>
                    {assetsOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="period"
            render={({ field }) => (
              <FormItem>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full mb-0">
                      <SelectValue placeholder="Select report version" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Select report version</SelectLabel>
                    </SelectGroup>
                    {getReportsVersions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>
        {form.formState.isDirty && (
          <Button type="button" size="sm" className="shrink-0" variant="ghost" onClick={() => form.reset()}>
            <X /> Clear
          </Button>
        )}
      </form>
    </Form>
  );
};

export default ReportSearch;
